import { Checkbox } from '@wellesley/ui'
import { FC, useState } from 'react'
import { toast } from '@wellesley/system-toast'
import { Rule, usePostApiV1ReportsMutation } from '@wellesley/openapi'
import { getErrorMsg } from '@wellesley/error-kit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import ReportLayout from '../ReportLayout'
import { TReportModal } from '../ReportModal/types'

type TServerRulesReport = Pick<
  TReportModal,
  'targetReportEntityId' | 'groupId'
> & {
  rules: Rule[]
  entity: 'group' | 'server'
  onSuccess(): void
}

const RulesReport: FC<TServerRulesReport> = ({
  rules,
  entity,
  groupId,
  onSuccess,
  targetReportEntityId,
}) => {
  const [rulesChecked, setRules] = useState([] as string[])

  const handleSelectRule = (ruleId: string) => {
    setRules((oldRules) => {
      let newRules = [...oldRules]

      if (newRules.includes(ruleId))
        newRules = newRules.filter((r) => r !== ruleId)
      else newRules.push(ruleId)

      return newRules
    })
  }

  const [makeReport, { isLoading }] = usePostApiV1ReportsMutation()

  const onSubmit = async () => {
    const comment = rulesChecked.reduce((acc, ruleId) => {
      const ruleText = rules?.find((r) => r.id === ruleId)?.text

      acc += `${ruleId}: ${ruleText} \n`

      return acc
    }, `Reporting violation of ${entity} rules \n`)

    try {
      await makeReport({
        createReport: {
          comment,
          groupId,
          type: entity === 'server' ? 'SERVER_RULES' : 'GROUP_RULES',
          targetId: targetReportEntityId,
          forward: true,
        },
      }).unwrap()
      onSuccess()
    } catch (error) {
      toast.error(getErrorMsg(error as FetchBaseQueryError) ?? '')
    }
  }

  const sortedRules = rules
    ? [...rules].sort((a, b) => a.ordering - b.ordering)
    : rules

  return (
    <ReportLayout
      isLoading={isLoading}
      isDisabledSubmit={rulesChecked.length === 0}
      onRightButtonClick={() => void onSubmit()}
      rightButtonTitle="Report"
    >
      <div className="max-h-[535px] overflow-y-auto text-gray-700">
        {sortedRules?.map((rule) => (
          <div
            key={rule.id}
            data-testid="rule-row"
            className="gap-xxs py-xxs flex"
            onClick={() => handleSelectRule(rule.id)}
          >
            <Checkbox
              className="pointer-events-none"
              value={rule.id}
              checked={rulesChecked.includes(rule.id)}
              onCheckedChange={() => {
                handleSelectRule(rule.id)
              }}
            />
            <div className="cursor-pointer flex-col">
              <h3 className="text-base font-semibold">{rule.text}</h3>
              <p className="text-sm text-gray-500">{rule.hint}</p>
            </div>
          </div>
        ))}
      </div>
    </ReportLayout>
  )
}

export default RulesReport
